import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import Button from '@fec/frontend/elements/buttons/button/button';
import SvgIcon from '@fec/frontend/elements/icons/svg-icon/svg-icon';
import './emergency-banner.scss';

const EmergencyBanner = ({ endpointUrl }) => {
  const { t } = useTranslation();

  const [isDismissed, setDismissed] = useState(false);

  const { data } = useQuery({
    queryKey: ['emergency-banner'],
    queryFn: async () => {
      const response = await fetch(endpointUrl);
      return await response.json();
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: endpointUrl && !isDismissed,
  });

  // no data - no banner
  if (!data || isDismissed || data?.isActive === false) {
    return null;
  }

  const { title = '', body = '', links = [] } = data;

  // inspired by https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/alertdialog_role#example_1_a_basic_alert_dialog
  return (
    <div className="emergency-banner" role="alertdialog">
      <div
        className="emergency-banner__content"
        role="document"
        tabIndex="0"
        ref={(el) => el && el.focus()}>
        <span className="h-offscreen">
          {t('emergencyBanner.a11yDescription')}
        </span>

        {/* close button should be top right, but last in the DOM - reserve the space here but position the real button absolutely */}
        <div className="emergency-banner__close-button-space-reserver"></div>

        <h2 className="emergency-banner__title">{title}</h2>
        <p className="emergency-banner__body">{body}</p>

        {links.length > 0 && (
          <ul className="emergency-banner__links">
            {links.map((link, index) => (
              <li key={index}>
                <a href={link.href} className="emergency-banner__link">
                  {link.text}
                  <SvgIcon styleModifier="svg-icon--arrow-right-white" />
                </a>
              </li>
            ))}
          </ul>
        )}

        <div className="emergency-banner__close-button-wrapper">
          <Button
            screenReaderText={t('emergencyBanner.closeButtonHint')}
            styleModifier="button--transparent button--align-left button--no-spacing button--icon-button"
            svgIcon="svg-icon--close"
            onClick={() => setDismissed(true)}
          />
        </div>
      </div>
    </div>
  );
};

export default EmergencyBanner;
